/* ============================================
   AFINA'S LITTLE CREATIONS — Theme JavaScript
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initScrollHeader();
  initMobileNav();
  initProductGallery();
  initQuantitySelectors();
  initAnnouncementBar();
  initCollectionCarousels();
  initCardAtcForms();
  initProductFormAjax();
  initVariantButtons();
  initCollectionSort();
  initFormDoubleSubmitGuard();
});

/* --- Collection page: auto-submit sort dropdown --- */
function initCollectionSort() {
  document.querySelectorAll('[data-collection-sort]').forEach((select) => {
    if (select.dataset.sortInit === '1') return;
    select.dataset.sortInit = '1';
    select.addEventListener('change', () => {
      /* Build URL from current location, set sort_by, drop pagination so we
         start at page 1 with the new sort. */
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });
}

/* --- Forms: prevent double-submission while a request is in flight --- */
let _doubleSubmitInitialized = false;

function initFormDoubleSubmitGuard() {
  if (_doubleSubmitInitialized) return;
  _doubleSubmitInitialized = true;
  /* Excludes our AJAX cart forms which manage their own state. */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.classList.contains('product-form--ajax')) return;
    if (form.classList.contains('product-card__atc-form')) return;

    const submitBtns = form.querySelectorAll('button[type="submit"], input[type="submit"]');
    /* Block re-entrant submits before we ever started. */
    for (const btn of submitBtns) {
      if (btn.disabled) {
        e.preventDefault();
        return;
      }
    }
    /* Disable AFTER the synchronous submit event so the button's name=value
       is still included in the form data sent to the server. */
    setTimeout(() => {
      submitBtns.forEach((btn) => {
        btn.disabled = true;
        btn.classList.add('btn--submitting');
      });
    }, 0);
    /* Re-enable after a generous timeout in case navigation never happens
       (e.g. validation server-side bounce, error response). */
    setTimeout(() => {
      submitBtns.forEach((btn) => {
        btn.disabled = false;
        btn.classList.remove('btn--submitting');
      });
    }, 8000);
  });
}

/* --- Sticky header shadow on scroll --- */
let _scrollHeaderInitialized = false;

function initScrollHeader() {
  if (_scrollHeaderInitialized) return;
  const header = document.querySelector('.site-header');
  if (!header) return;
  _scrollHeaderInitialized = true;

  const observer = new IntersectionObserver(
    ([entry]) => header.classList.toggle('scrolled', !entry.isIntersecting),
    { rootMargin: '-1px 0px 0px 0px', threshold: 0 }
  );

  const sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
  document.body.prepend(sentinel);
  observer.observe(sentinel);
}

/* --- Mobile Navigation Drawer --- */
let _mobileNavInitialized = false;

function initMobileNav() {
  if (_mobileNavInitialized) return;
  const toggle = document.querySelector('.nav-toggle');
  const drawer = document.querySelector('.nav-drawer');
  if (!toggle || !drawer) return;
  _mobileNavInitialized = true;

  const backdrop = drawer.querySelector('.nav-drawer__backdrop');
  const closeBtn = drawer.querySelector('.nav-drawer__close');
  const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

  const focusables = () =>
    Array.from(drawer.querySelectorAll(focusableSelector)).filter((el) => !el.closest('[hidden]'));

  const open = () => {
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    /* Move focus into drawer so subsequent Tab keys stay inside it. */
    const first = focusables()[0];
    if (first) first.focus();
  };

  const close = () => {
    if (!drawer.classList.contains('open')) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    /* Restore focus to the toggle so keyboard users aren't lost. */
    toggle.focus();
  };

  toggle.addEventListener('click', open);
  backdrop?.addEventListener('click', close);
  closeBtn?.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (!drawer.classList.contains('open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
}

/* --- Product Gallery Thumbnails --- */
let productGalleryFadeTimer = null;

function swapMainProductImage({ src, srcset, alt }) {
  const mainImg = document.querySelector('.product-gallery__main img');
  if (!mainImg || !src) return;

  /* Cancel any pending fade so rapid clicks don't stack timeouts. */
  if (productGalleryFadeTimer) clearTimeout(productGalleryFadeTimer);

  mainImg.style.transition = 'opacity 0.2s ease';
  mainImg.style.opacity = '0';

  productGalleryFadeTimer = setTimeout(() => {
    /* Set srcset BEFORE src so the browser picks the right candidate from the set. */
    if (srcset) mainImg.setAttribute('srcset', srcset);
    else mainImg.removeAttribute('srcset');
    mainImg.src = src;
    if (alt) mainImg.alt = alt;
    mainImg.style.opacity = '1';
    productGalleryFadeTimer = null;
  }, 180);
}

let _productGalleryInitialized = false;

function initProductGallery() {
  if (_productGalleryInitialized) return;
  const thumbs = document.querySelectorAll('.product-gallery__thumb');
  const mainImg = document.querySelector('.product-gallery__main img');
  if (!thumbs.length || !mainImg) return;
  _productGalleryInitialized = true;

  const activate = (thumb) => {
    swapMainProductImage({
      src: thumb.dataset.src,
      srcset: thumb.dataset.srcset,
      alt: thumb.querySelector('img')?.alt,
    });
    thumbs.forEach((t) => t.classList.remove('active'));
    thumb.classList.add('active');
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => activate(thumb));
    /* Keyboard activation for thumbs that have role="button" tabindex="0". */
    thumb.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate(thumb);
      }
    });
  });
}

/* --- Quantity Selector Buttons --- */
function initQuantitySelectors() {
  document.querySelectorAll('.quantity-selector').forEach((selector) => {
    if (selector.dataset.qtyInit === '1') return;
    selector.dataset.qtyInit = '1';
    const input = selector.querySelector('.quantity-input');
    const decreaseBtn = selector.querySelector('[data-action="decrease"]');
    const increaseBtn = selector.querySelector('[data-action="increase"]');

    if (!input) return;

    /* On the cart page the input has name="updates[]" and reaching 0 is the documented
       way to remove a line. On the PDP the input is "quantity" and 0 is invalid for
       /cart/add.js, so we floor at 1 there. */
    const onCartPage = input.name === 'updates[]' || !!selector.closest('.cart-page');

    decreaseBtn?.addEventListener('click', () => {
      const current = parseInt(input.value, 10) || 1;
      if (current > 1) {
        input.value = current - 1;
      } else {
        input.value = onCartPage ? 0 : 1;
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    increaseBtn?.addEventListener('click', () => {
      const current = parseInt(input.value, 10) || 0;
      const max = parseInt(input.max, 10) || Infinity;
      if (current < max) input.value = current + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    /* Sanitize manual typing: clamp into [min, max] on blur/change */
    input.addEventListener('change', () => {
      const min = onCartPage ? 0 : (parseInt(input.min, 10) || 1);
      const max = parseInt(input.max, 10);
      let val = parseInt(input.value, 10);
      if (!Number.isFinite(val)) val = min;
      if (val < min) val = min;
      if (Number.isFinite(max) && val > max) val = max;
      if (String(val) !== input.value) input.value = val;
    });
  });

  /* Cart page: save every quantity change to Shopify via AJAX */
  initCartQuantitySync();
}

let _cartSyncInitialized = false;

function initCartQuantitySync() {
  if (_cartSyncInitialized) return;
  const cartPage = document.querySelector('.cart-page');
  if (!cartPage) return;
  _cartSyncInitialized = true;

  /* One debounce timer per line (key) so concurrent changes on different rows
     don't cancel each other and we don't depend on a stale line index. */
  const debounceByKey = new Map();

  cartPage.addEventListener('change', (e) => {
    const input = e.target.closest('.quantity-input');
    if (!input) return;

    const row = input.closest('.cart-item');
    if (!row) return;

    const key = row.dataset.lineKey;
    if (!key) return;

    const newQty = Math.max(0, parseInt(input.value, 10) || 0);

    clearTimeout(debounceByKey.get(key));
    debounceByKey.set(key, setTimeout(() => updateLine(row, key, newQty), 350));
  });

  cartPage.addEventListener('click', (e) => {
    const removeLink = e.target.closest('[data-cart-remove], .cart-item__remove');
    if (!removeLink) return;
    e.preventDefault();
    const row = removeLink.closest('.cart-item');
    const key = row?.dataset.lineKey;
    if (!row || !key) return;
    updateLine(row, key, 0);
  });

  async function updateLine(row, key, newQty) {
    setCartLoading(cartPage, true);
    clearCartError();

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: newQty }),
      });

      if (!res.ok) {
        let serverMsg = '';
        try { const errBody = await res.json(); serverMsg = errBody.description || errBody.message || ''; } catch {}
        throw new Error(serverMsg || 'We could not update that item. Please try again.');
      }

      const cart = await res.json();
      updateCartCount(cart.item_count);

      const lineItem = (cart.items || []).find((i) => i.key === key);

      if (newQty === 0 || !lineItem) {
        row.style.transition = 'opacity 0.25s, transform 0.25s';
        row.style.opacity = '0';
        row.style.transform = 'translateX(-8px)';
        setTimeout(() => row.remove(), 260);

        if (cart.item_count === 0) {
          setTimeout(() => location.reload(), 320);
          return;
        }
      } else {
        const priceEl = row.querySelector('.cart-item__price');
        if (priceEl) {
          priceEl.textContent = formatMoney(lineItem.final_line_price ?? lineItem.line_price);
        }
        row.dataset.prevQty = String(lineItem.quantity);
        const input = row.querySelector('.quantity-input');
        if (input && parseInt(input.value, 10) !== lineItem.quantity) {
          input.value = lineItem.quantity;
        }
      }

      updateCartSummary(cart);
    } catch (err) {
      const prev = parseInt(row.dataset.prevQty, 10);
      const input = row.querySelector('.quantity-input');
      if (input && Number.isFinite(prev)) input.value = prev;
      showCartError(err && err.message ? err.message : 'Something went wrong. Please try again.');
      console.warn('[cart] update failed:', err);
    } finally {
      setCartLoading(cartPage, false);
    }
  }
}

function showCartError(message) {
  const el = document.querySelector('[data-cart-error]');
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
}

function clearCartError() {
  const el = document.querySelector('[data-cart-error]');
  if (!el) return;
  el.textContent = '';
  el.hidden = true;
}

function updateCartSummary(cart) {
  /* Targeted data-attribute selectors are reliable; the previous selector chain
     was fragile and broke if the markup changed. */
  const subtotal = formatMoney(cart.items_subtotal_price ?? cart.total_price);
  const total = formatMoney(cart.total_price);

  document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
    el.textContent = subtotal;
  });
  document.querySelectorAll('[data-cart-total]').forEach((el) => {
    el.textContent = total;
  });
}

function setCartLoading(cartPage, loading) {
  cartPage.style.opacity = loading ? '0.6' : '1';
  cartPage.style.pointerEvents = loading ? 'none' : '';
  cartPage.style.transition = 'opacity 0.15s';
}

/* --- Featured collection: horizontal carousel (progress + arrows) --- */
const collectionCarouselRoots = new WeakSet();

function setupCollectionCarousel(root) {
  if (!root || collectionCarouselRoots.has(root)) return;

  const viewport = root.querySelector('[data-carousel-viewport]');
  const footer = root.querySelector('[data-carousel-footer]');
  const thumb = root.querySelector('[data-carousel-thumb]');
  const prev = root.querySelector('[data-carousel-prev]');
  const next = root.querySelector('[data-carousel-next]');
  const track = root.querySelector('.product-grid--carousel');

  if (!viewport || !footer || !thumb || !prev || !next || !track) return;

  collectionCarouselRoots.add(root);

  const getGap = () => {
    const g = getComputedStyle(track).gap || getComputedStyle(track).columnGap;
    const n = parseFloat(g);
    return Number.isFinite(n) ? n : 24;
  };

  const scrollStep = () => {
    const card = track.querySelector('.product-card');
    if (!card) return viewport.clientWidth * 0.85;
    return card.getBoundingClientRect().width + getGap();
  };

  const hasOverflow = () =>
    Math.ceil(viewport.scrollWidth) > Math.floor(viewport.clientWidth) + 1;

  const update = () => {
    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const scrollable = hasOverflow();

    footer.hidden = !scrollable;
    if (!scrollable) {
      thumb.style.width = '';
      thumb.style.left = '';
      return;
    }

    const thumbW = (clientWidth / scrollWidth) * 100;
    const maxLeft = 100 - thumbW;
    const left = maxScroll <= 0 ? 0 : (scrollLeft / maxScroll) * maxLeft;

    thumb.style.width = `${thumbW}%`;
    thumb.style.left = `${left}%`;

    prev.disabled = scrollLeft <= 1;
    next.disabled = scrollLeft >= maxScroll - 1;
  };

  prev.addEventListener('click', () => {
    viewport.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
  });

  next.addEventListener('click', () => {
    viewport.scrollBy({ left: scrollStep(), behavior: 'smooth' });
  });

  viewport.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      viewport.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      viewport.scrollBy({ left: scrollStep(), behavior: 'smooth' });
    }
  });

  viewport.addEventListener('scroll', update, { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => update());
    ro.observe(viewport);
    ro.observe(track);
  }

  window.addEventListener('resize', update, { passive: true });

  window.addEventListener('load', update, { passive: true });

  track.querySelectorAll('img').forEach((img) => {
    if (!img.complete) img.addEventListener('load', update, { passive: true });
  });

  requestAnimationFrame(() => {
    update();
    requestAnimationFrame(update);
  });
}

function initCollectionCarousels() {
  document.querySelectorAll('[data-collection-carousel]').forEach(setupCollectionCarousel);

  document.addEventListener('shopify:section:load', (e) => {
    e.target?.querySelectorAll?.('[data-collection-carousel]').forEach(setupCollectionCarousel);
  });
}

/* --- Announcement Bar dismiss --- */
let _announcementBarInitialized = false;

function initAnnouncementBar() {
  if (_announcementBarInitialized) return;
  const bar = document.querySelector('.announcement-bar');
  const dismiss = bar?.querySelector('[data-dismiss]');
  if (!dismiss) return;
  _announcementBarInitialized = true;

  dismiss.addEventListener('click', () => {
    bar.style.maxHeight = bar.offsetHeight + 'px';
    requestAnimationFrame(() => {
      bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
      bar.style.maxHeight = '0';
      bar.style.overflow = 'hidden';
      bar.style.opacity = '0';
    });
  });
}

/* --- Card: AJAX Add To Cart + Quantity Stepper --- */
let _cardAtcInitialized = false;

function initCardAtcForms() {
  /* Sync existing cart state into any card steppers on page load */
  syncCardsWithCart();

  /* Listeners are delegated and only attached once, even if init runs again
     (e.g. via Shopify's section-load event in the editor). */
  if (_cardAtcInitialized) return;
  _cardAtcInitialized = true;

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.product-card__atc-form');
    if (!form) return;

    e.preventDefault();

    const bar = form.closest('[data-atc]');
    const btn = form.querySelector('.product-card__atc-btn');
    if (!bar || !btn || btn.disabled) return;

    const variantId = bar.dataset.variantId;
    if (!variantId) return;

    spawnRipple(btn);

    btn.disabled = true;
    btn.classList.add('product-card__atc-btn--loading');
    const label = form.querySelector('.product-card__atc-label');
    const originalText = label ? label.textContent.trim() : 'Add To Cart';
    if (label) label.textContent = 'Adding…';

    try {
      const body = new URLSearchParams(new FormData(form)).toString();
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      });
      if (!res.ok) throw new Error('Add to cart failed');

      const addedItem = await res.json();
      /* Capture the line key so future +/- actions target this exact line, not
         every line of the same variant (which would corrupt carts with multiple
         lines of the same variant — e.g. with custom properties). */
      if (addedItem && addedItem.key) {
        bar.dataset.lineKey = addedItem.key;
      }

      await refreshCartAndBadge();
      setCardState(bar, addedItem?.quantity || 1, { animate: true });
    } catch (err) {
      console.warn('[card-atc] add failed:', err);
      btn.classList.remove('product-card__atc-btn--loading');
      if (label) label.textContent = 'Try Again';
      btn.disabled = false;
      setTimeout(() => { if (label) label.textContent = originalText; btn.disabled = false; }, 1800);
    }
  });

  document.addEventListener('click', async (e) => {
    const incBtn = e.target.closest('[data-atc-inc]');
    const decBtn = e.target.closest('[data-atc-dec]');
    const stepBtn = incBtn || decBtn;
    if (!stepBtn) return;

    const bar = stepBtn.closest('[data-atc]');
    if (!bar) return;

    e.preventDefault();
    e.stopPropagation();

    const variantId = bar.dataset.variantId;
    const lineKey = bar.dataset.lineKey;
    if (!variantId) return;

    const countEl = bar.querySelector('[data-atc-count]');
    const currentQty = parseInt(countEl?.textContent || '1', 10) || 1;
    const newQty = incBtn ? currentQty + 1 : Math.max(0, currentQty - 1);

    bar.querySelectorAll('.product-card__atc-step').forEach((b) => b.disabled = true);

    try {
      let res;
      if (lineKey) {
        /* Line-key targeting: only this line is changed, even if multiple lines
           of the same variant exist in the cart. */
        res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: lineKey, quantity: newQty }),
        });
      } else {
        /* Fallback when we don't yet know the line key (page rendered without
           one and user hasn't clicked add yet). Add the missing units. */
        res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: newQty - currentQty }),
        });
      }
      if (!res.ok) throw new Error('Cart update failed');

      const cart = await refreshCartAndBadge();
      /* Update the bar's cached line key from the latest cart in case it changed. */
      if (cart && cart.items) {
        const matchKey = bar.dataset.lineKey;
        const item = matchKey
          ? cart.items.find((i) => i.key === matchKey)
          : cart.items.find((i) => i.variant_id === parseInt(variantId, 10));
        if (item) {
          bar.dataset.lineKey = item.key;
          setCardState(bar, item.quantity, { animate: true });
        } else {
          delete bar.dataset.lineKey;
          setCardState(bar, 0, { animate: true });
        }
      }
    } catch (err) {
      console.warn('[card-atc] step failed:', err);
    } finally {
      bar.querySelectorAll('.product-card__atc-step').forEach((b) => b.disabled = false);
    }
  });
}

/* Toggle a product card between "Add to cart" form and the qty stepper */
function setCardState(bar, qty, { animate = false } = {}) {
  if (!bar) return;
  const form = bar.querySelector('[data-atc-add]');
  const stepper = bar.querySelector('[data-atc-stepper]');
  const countEl = bar.querySelector('[data-atc-count]');
  const decBtn = bar.querySelector('[data-atc-dec]');
  const btn = bar.querySelector('.product-card__atc-btn');
  const label = bar.querySelector('.product-card__atc-label');

  if (qty > 0) {
    /* show stepper */
    if (form) form.hidden = true;
    if (stepper) stepper.hidden = false;
    if (countEl) {
      const prev = parseInt(countEl.textContent || '0', 10) || 0;
      countEl.textContent = qty;
      if (animate && prev !== qty) {
        countEl.classList.remove('product-card__atc-count--bump');
        void countEl.offsetWidth;
        countEl.classList.add('product-card__atc-count--bump');
      }
    }
    if (decBtn) decBtn.classList.toggle('is-trash', qty === 1);
    bar.classList.add('product-card__atc-bar--active');

    /* reset button text/state in case we flip back later */
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('product-card__atc-btn--loading');
    }
    if (label) label.textContent = 'Add To Cart';
  } else {
    /* show "Add to Cart" form */
    if (stepper) stepper.hidden = true;
    if (form) form.hidden = false;
    if (countEl) countEl.textContent = 1;
    if (decBtn) decBtn.classList.remove('is-trash');
    bar.classList.remove('product-card__atc-bar--active');

    if (btn) {
      btn.disabled = false;
      btn.classList.remove('product-card__atc-btn--loading');
    }
    if (label) label.textContent = 'Add To Cart';
  }
}

/* Spawn the accent ripple circle on the add-to-cart button */
function spawnRipple(btn) {
  const ripple = document.createElement('span');
  ripple.className = 'product-card__atc-ripple';
  const rect = btn.getBoundingClientRect();
  ripple.style.cssText = `left:${rect.width / 2 - 30}px;top:${rect.height / 2 - 30}px;`;
  btn.style.position = 'relative';
  btn.appendChild(ripple);
  ripple.addEventListener('animationend', () => ripple.remove());
}

/* Fetch current cart, update the header badge, and sync card steppers */
async function refreshCartAndBadge() {
  try {
    const res = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!res.ok) return null;
    const cart = await res.json();
    updateCartCount(cart.item_count);
    syncCardsWithCart(cart);
    return cart;
  } catch (err) {
    console.warn('[cart] refresh failed:', err);
    return null;
  }
}

/* Walk all product cards and set their stepper state based on the cart */
async function syncCardsWithCart(cart) {
  const cards = document.querySelectorAll('[data-atc]');
  if (!cards.length) return;

  if (!cart) {
    try {
      const res = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) return;
      cart = await res.json();
    } catch (err) { console.warn('[cart] sync failed:', err); return; }
  }

  /* Build a map: variantId -> array of line items (most-recent first). */
  const linesByVariant = new Map();
  (cart.items || []).forEach((item) => {
    const arr = linesByVariant.get(item.variant_id) || [];
    arr.push(item);
    linesByVariant.set(item.variant_id, arr);
  });

  cards.forEach((bar) => {
    const id = parseInt(bar.dataset.variantId, 10);
    const lines = linesByVariant.get(id) || [];
    if (lines.length === 0) {
      delete bar.dataset.lineKey;
      setCardState(bar, 0);
      return;
    }
    /* Prefer the line we already track if it's still in the cart; otherwise
       pick the first line for this variant. */
    const existingKey = bar.dataset.lineKey;
    const tracked = existingKey ? lines.find((l) => l.key === existingKey) : null;
    const line = tracked || lines[0];
    bar.dataset.lineKey = line.key;
    setCardState(bar, line.quantity);
  });
}

/* --- Product page: AJAX add to cart --- */
let _productFormAjaxInitialized = false;

function initProductFormAjax() {
  if (_productFormAjaxInitialized) return;
  _productFormAjaxInitialized = true;
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.product-form--ajax');
    if (!form) return;

    e.preventDefault();

    const btn = form.querySelector('.product-form__submit');
    const label = form.querySelector('.product-form__submit-label');
    if (!btn || !label || btn.disabled) return;

    const originalText = label.textContent.trim();
    btn.disabled = true;
    btn.classList.add('product-form__submit--loading');
    label.textContent = 'Adding…';

    try {
      const body = new URLSearchParams(new FormData(form)).toString();
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body,
      });

      if (!res.ok) throw new Error('Add to cart failed');

      btn.classList.remove('product-form__submit--loading');
      btn.classList.add('product-form__submit--success');
      label.textContent = 'Added to Cart!';

      await refreshCartAndBadge();

      setTimeout(() => {
        btn.classList.remove('product-form__submit--success');
        /* Only reset if applyVariant hasn't changed the state while we were animating
           (e.g. user switched to a sold-out variant during the success window) */
        if (label.textContent.trim() === 'Added to Cart!') {
          label.textContent = originalText;
          btn.disabled = false;
        }
      }, 1800);

    } catch (err) {
      console.warn('[product-form] add failed:', err);
      btn.classList.remove('product-form__submit--loading');
      label.textContent = 'Try Again';
      btn.disabled = false;
      setTimeout(() => {
        if (label.textContent.trim() === 'Try Again') {
          label.textContent = originalText;
        }
      }, 1800);
    }
  });
}

/* --- Product page: variant button selection --- */
let _variantButtonsInitialized = false;

function initVariantButtons() {
  if (_variantButtonsInitialized) return;
  const form = document.querySelector('[data-product-form]');
  if (!form) return;

  const variantDataEl = document.querySelector('[id^="ProductVariantData-"]');
  if (!variantDataEl) return;
  _variantButtonsInitialized = true;
  let variants;
  try { variants = JSON.parse(variantDataEl.textContent); } catch { return; }

  const variantInput = document.querySelector('[id^="SelectedVariantId-"]');
  if (!variantInput) return;

  const groups = Array.from(document.querySelectorAll('[data-option-group]'));
  const selectedOptions = [];
  groups.forEach((group) => {
    const idx = parseInt(group.dataset.optionGroup, 10);
    const activeBtn = group.querySelector('.variant-btn.active');
    selectedOptions[idx] =
      activeBtn ? activeBtn.dataset.value : group.querySelector('.variant-btn')?.dataset.value;
  });

  /* Resolve the variant matching the current full selection. */
  function resolveVariant() {
    return variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    ) || null;
  }

  /* Find any variant that matches the option values at the given indexes
     (the current selection minus the option being tested). */
  function findVariantWithOptions(partial) {
    return variants.find((v) =>
      Object.entries(partial).every(([i, val]) => v.options[i] === val)
    );
  }

  /* For each option pill, "ghost" the ones that have no available combo with
     the rest of the current selection (Dawn's approach). */
  const optionCount = variants[0]?.options?.length || 0;

  function refreshAvailability() {
    groups.forEach((group) => {
      const optionIndex = parseInt(group.dataset.optionGroup, 10);
      group.querySelectorAll('.variant-btn').forEach((btn) => {
        const partial = {};
        for (let i = 0; i < optionCount; i++) {
          if (i === optionIndex) partial[i] = btn.dataset.value;
          else if (selectedOptions[i] != null) partial[i] = selectedOptions[i];
        }
        const match = findVariantWithOptions(partial);
        const exists = !!match;
        const available = !!(match && match.available);
        btn.classList.toggle('variant-btn--unavailable', !exists);
        btn.classList.toggle('variant-btn--soldout', exists && !available);
        if (!exists) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    });
  }

  function formatPriceCents(cents) {
    return formatMoney(cents);
  }

  function applyVariant(variant) {
    if (!variant) {
      /* No matching variant: still surface availability so user sees ghosted pills. */
      refreshAvailability();
      const submitBtn = form.querySelector('.product-form__submit');
      const submitLabel = form.querySelector('.product-form__submit-label');
      if (submitBtn && submitLabel) {
        submitBtn.disabled = true;
        submitLabel.textContent = 'Unavailable';
      }
      return;
    }

    variantInput.value = variant.id;

    /* Update URL so refresh / share preserves the selection. */
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch {}

    /* Add to cart button state. */
    const submitBtn = form.querySelector('.product-form__submit');
    const submitLabel = form.querySelector('.product-form__submit-label');
    if (submitBtn && submitLabel) {
      if (variant.available) {
        submitBtn.disabled = false;
        submitLabel.textContent = 'Add to Cart';
      } else {
        submitBtn.disabled = true;
        submitLabel.textContent = 'Sold Out';
      }
    }

    /* Price + compare-at price. */
    const priceEl = document.querySelector('[data-price]');
    const compareEl = document.querySelector('[data-compare-price]');
    if (priceEl) {
      priceEl.textContent = formatPriceCents(variant.price);
      priceEl.classList.toggle('price--sale', variant.compareAtPrice && variant.compareAtPrice > variant.price);
    }
    if (compareEl) {
      if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        compareEl.textContent = formatPriceCents(variant.compareAtPrice);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    /* Quantity input bounds based on inventory. */
    const qtyInput = form.querySelector('input[name="quantity"]');
    if (qtyInput) {
      const tracked = variant.inventoryManagement && variant.inventoryPolicy === 'deny';
      if (tracked && Number.isFinite(variant.inventoryQuantity) && variant.inventoryQuantity > 0) {
        qtyInput.max = String(variant.inventoryQuantity);
        const current = parseInt(qtyInput.value, 10) || 1;
        if (current > variant.inventoryQuantity) qtyInput.value = String(variant.inventoryQuantity);
      } else {
        qtyInput.removeAttribute('max');
      }
      qtyInput.min = '1';
    }

    /* Variant image swap (with srcset). */
    if (variant.featuredImageSrc) {
      swapMainProductImage({
        src: variant.featuredImageSrc,
        srcset: variant.featuredImageSrcset,
        alt: variant.featuredImageAlt,
      });
      document.querySelectorAll('.product-gallery__thumb').forEach((t) => {
        t.classList.toggle('active', t.dataset.imageId === String(variant.featuredImageId));
      });
    }

    refreshAvailability();
  }

  groups.forEach((group) => {
    group.addEventListener('click', (e) => {
      const btn = e.target.closest('.variant-btn');
      if (!btn) return;
      /* Allow clicking unavailable combos so the customer can see what other
         options pair with them (Dawn does this). The ATC just shows Sold Out. */
      if (btn.getAttribute('aria-disabled') === 'true' && btn.classList.contains('variant-btn--unavailable')) {
        /* Clicking a hard-unavailable combo: still update selection so they can
           backtrack, but don't try to resolve a non-existent variant. */
      }

      const optionIndex = parseInt(group.dataset.optionGroup, 10);
      group.querySelectorAll('.variant-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');

      const labelSpan = group.closest('.product-option')?.querySelector('[data-selected-value]');
      if (labelSpan) labelSpan.textContent = btn.dataset.value;

      selectedOptions[optionIndex] = btn.dataset.value;
      applyVariant(resolveVariant());
    });
  });

  applyVariant(resolveVariant());
}

/* --- Cart count badge update --- */
function updateCartCount(count) {
  let badge = document.querySelector('.cart-count');

  /* If the badge was never rendered (cart was 0 on page load), create it */
  if (!badge) {
    const cartLink = document.querySelector('a[href="/cart"], a[aria-label*="Cart"]');
    if (cartLink) {
      badge = document.createElement('span');
      badge.className = 'cart-count cart-count--hidden';
      badge.setAttribute('aria-live', 'polite');
      cartLink.appendChild(badge);
    }
  }

  if (!badge) return;

  badge.textContent = count;

  if (count > 0) {
    badge.classList.remove('cart-count--hidden');
    /* pop animation */
    badge.classList.remove('cart-count--pop');
    void badge.offsetWidth; /* reflow to restart animation */
    badge.classList.add('cart-count--pop');
    badge.addEventListener('animationend', () => badge.classList.remove('cart-count--pop'), { once: true });
  } else {
    badge.classList.add('cart-count--hidden');
  }
}

/* --- Format money helper (Shopify returns cents as integers).
   Uses shop.money_format injected by theme.liquid so currencies always render
   consistently with Liquid's `| money` filter (e.g. CAD, EUR, GBP shops). */
function formatMoney(cents) {
  const fmt =
    (typeof window !== 'undefined' && window.shopifyMoneyFormat) ||
    (typeof Shopify !== 'undefined' && Shopify.money_format);

  if (fmt) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100);
    const amountWithComma = amount.replace('.', ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(amountNoDecimals));
  }

  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);
}
